import type { BundleFile, ExpertSummary } from "../../api";
import { ExpertMark } from "../design/ExpertMark";

const KIND_GLYPH: Record<string, { letter: string; color: string }> = {
  md: { letter: "M", color: "spec" },
  csv: { letter: "C", color: "ctd" },
  mermaid: { letter: "G", color: "sys" },
  diff: { letter: "Δ", color: "aws" },
  json: { letter: "J", color: "gdpr" },
  txt: { letter: "T", color: "ink-3" },
  folder: { letter: "F", color: "sys" },
};

export type BundleTreeSelection = {
  /** Full zip-entry path, e.g. `MEETING.md` or
   *  `software-spec-author/proposal.md`. Carried verbatim to the
   *  `GET /v1/jobs/{id}/artifact?path=…` endpoint. */
  path: string;
};

/** Left-rail bundle tree for the Finalized view. Renders from the dynamic
 *  `GET /v1/jobs/{id}/files` listing — root files first, then per-expert
 *  subfolders (only experts with ≥ 1 file in the listing), then an
 *  "Other" bucket for anything else (e.g. `frames/`).
 */
export function BundleTree({
  files,
  experts,
  active,
  onSelect,
}: {
  files: BundleFile[];
  experts: ExpertSummary[];
  active: BundleTreeSelection | null;
  onSelect: (s: BundleTreeSelection) => void;
}) {
  const { root, perExpert, other } = groupBundleFiles(files, experts);
  return (
    <div className="mono" style={{ fontSize: 12 }}>
      {root.map((f) => (
        <TreeRow
          key={f.path}
          name={f.path}
          kind={kindFromPath(f.path)}
          active={active?.path === f.path}
          onClick={() => onSelect({ path: f.path })}
        />
      ))}

      {experts.map((e) => {
        const list = perExpert.get(e.id);
        if (!list || list.length === 0) return null;
        return (
          <ExpertGroup
            key={e.id}
            expert={e}
            files={list}
            active={active}
            onSelect={onSelect}
          />
        );
      })}

      {other.length > 0 && (
        <>
          <div style={{ height: 14 }} />
          <div
            className="eyebrow"
            style={{ marginBottom: 6, fontSize: 9.5 }}
          >
            Other
          </div>
          {other.map((f) => (
            <TreeRow
              key={f.path}
              name={f.path}
              kind={kindFromPath(f.path)}
              active={active?.path === f.path}
              onClick={() => onSelect({ path: f.path })}
            />
          ))}
        </>
      )}
    </div>
  );
}

/** Split the listing into three buckets by first path segment: root (no
 *  `/`), per-expert (segment matches a known expert id), other (anything
 *  else, e.g. `frames/...`). Per-expert preserves listing order; group
 *  iteration order is fixed to the `experts` prop's order so the tree
 *  matches the meeting's roster. */
export function groupBundleFiles(
  files: BundleFile[],
  experts: ExpertSummary[],
): {
  root: BundleFile[];
  perExpert: Map<string, BundleFile[]>;
  other: BundleFile[];
} {
  const expertIds = new Set(experts.map((e) => e.id));
  const root: BundleFile[] = [];
  const perExpert = new Map<string, BundleFile[]>();
  const other: BundleFile[] = [];
  for (const f of files) {
    const slash = f.path.indexOf("/");
    if (slash === -1) {
      root.push(f);
      continue;
    }
    const head = f.path.slice(0, slash);
    if (expertIds.has(head)) {
      const list = perExpert.get(head) ?? [];
      list.push(f);
      perExpert.set(head, list);
    } else {
      other.push(f);
    }
  }
  return { root, perExpert, other };
}

/** Infer a renderer kind from the file's extension. Mirrors the api's
 *  `guess_content_type` taxonomy. */
export function kindFromPath(path: string): string {
  if (path.endsWith(".mmd")) return "mermaid";
  if (path.endsWith(".md")) return "md";
  if (path.endsWith(".csv")) return "csv";
  if (path.endsWith(".diff") || path.endsWith(".patch")) return "diff";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".txt")) return "txt";
  if (
    path.endsWith(".jpg") ||
    path.endsWith(".jpeg") ||
    path.endsWith(".png") ||
    path.endsWith(".webp") ||
    path.endsWith(".gif")
  ) {
    return "image";
  }
  if (path.endsWith("/")) return "folder";
  return "md";
}

function ExpertGroup({
  expert,
  files,
  active,
  onSelect,
}: {
  expert: ExpertSummary;
  files: BundleFile[];
  active: BundleTreeSelection | null;
  onSelect: (s: BundleTreeSelection) => void;
}) {
  const expanded = files.some((f) => f.path === active?.path);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        type="button"
        onClick={() => onSelect({ path: files[0].path })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          textAlign: "left",
          padding: "6px 8px",
          borderRadius: 5,
          border: 0,
          cursor: "pointer",
          background: expanded ? "var(--card)" : "transparent",
          fontFamily: "inherit",
        }}
      >
        <ExpertMark expert={expert} size={20} />
        <span style={{ fontSize: 12, fontWeight: 500 }}>
          {expert.short_name}/
        </span>
      </button>
      {expanded &&
        files.map((f) => {
          const isActive = active?.path === f.path;
          const kind = kindFromPath(f.path);
          const glyph = KIND_GLYPH[kind] ?? KIND_GLYPH.md;
          const filename = f.path.slice(expert.id.length + 1);
          return (
            <button
              key={f.path}
              type="button"
              onClick={() => onSelect({ path: f.path })}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                textAlign: "left",
                padding: "4px 8px 4px 28px",
                borderRadius: 5,
                border: 0,
                cursor: "pointer",
                background: isActive
                  ? `var(--${expert.color_token}-tint)`
                  : "transparent",
                color: isActive
                  ? `var(--${expert.color_token}-deep)`
                  : "var(--ink-2)",
                fontFamily: "var(--mono)",
                fontSize: 11.5,
              }}
            >
              <KindBadge kind={glyph} />
              {filename}
            </button>
          );
        })}
    </div>
  );
}

function TreeRow({
  name,
  kind,
  active,
  onClick,
}: {
  name: string;
  kind: string;
  active: boolean;
  onClick: () => void;
}) {
  const glyph = KIND_GLYPH[kind] ?? KIND_GLYPH.md;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        textAlign: "left",
        padding: "4px 8px",
        borderRadius: 5,
        border: 0,
        cursor: "pointer",
        background: active ? "var(--card)" : "transparent",
        fontFamily: "inherit",
        fontSize: 12,
      }}
    >
      <KindBadge kind={glyph} />
      <span>{name}</span>
    </button>
  );
}

function KindBadge({
  kind,
}: {
  kind: { letter: string; color: string };
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 3,
        background: `var(--${kind.color}-tint)`,
        color: `var(--${kind.color}-deep)`,
        fontFamily: "var(--mono)",
        fontSize: 10,
        fontWeight: 700,
        border: `1px solid color-mix(in oklab, var(--${kind.color}) 30%, transparent)`,
      }}
    >
      {kind.letter}
    </span>
  );
}
